import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ChatBubble from "./ChatBubble";
import {
  LayoutDashboard,
  Users,
  UserCog,
  ClipboardList,
  Package,
  Trash2,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/orders", label: "Orders & Dispatch", icon: ClipboardList, testid: "nav-orders" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/parties", label: "Buyers (Parties)", icon: Users, testid: "nav-parties" },
  { to: "/employees", label: "Employees", icon: UserCog, testid: "nav-employees" },
  { to: "/trash", label: "Recycle Bin", icon: Trash2, testid: "nav-trash" },
];

function SidebarContent({ user, onNavClick, onLogout }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6">
        <Link
          to="/"
          data-testid="brand-home-link"
          onClick={onNavClick}
          className="flex items-center gap-3 group"
        >
          <div className="h-11 w-11 rounded-xl bg-forest text-white grid place-items-center font-outfit font-bold text-lg transition-transform duration-200 group-hover:-translate-y-0.5 flex-shrink-0">
            H
          </div>
          <div className="min-w-0">
            <div className="font-outfit font-bold text-ink text-[15px] leading-tight group-hover:text-forest transition-colors duration-200 truncate">
              Hygenix Healthcare
            </div>
            <div className="font-figtree text-xs text-ink-soft leading-tight mt-0.5">Order &amp; Dispatch</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={onNavClick}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-4 py-3 rounded-lg font-figtree font-medium text-[15px]",
                "transition-colors duration-200",
                isActive
                  ? "bg-forest-soft text-forest"
                  : "text-ink-soft hover:bg-forest-soft/60 hover:text-forest",
              ].join(" ")
            }
          >
            <item.icon className="h-5 w-5" strokeWidth={1.75} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-line">
        <div className="px-3 py-2 mb-2">
          <div className="font-figtree text-xs text-ink-soft">Signed in as</div>
          <div className="font-outfit font-semibold text-ink truncate">{user?.email}</div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-4 py-3 rounded-lg text-ink-soft hover:bg-red-50 hover:text-red-700 transition-colors duration-200 font-figtree font-medium"
        >
          <LogOut className="h-5 w-5" strokeWidth={1.75} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const sidebarProps = {
    user,
    onNavClick: () => setOpen(false),
    onLogout: handleLogout,
  };

  return (
    <div className="min-h-screen bg-bone flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-72 bg-sand border-r border-line flex-col fixed inset-y-0">
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-bone border-b border-line px-4 py-3 flex items-center justify-between">
        <Link to="/" data-testid="brand-home-link-mobile" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-forest text-white grid place-items-center font-outfit font-bold">H</div>
          <div className="font-outfit font-bold text-ink">Hygenix Healthcare</div>
        </Link>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(true)}
          className="p-2 rounded-lg hover:bg-sand"
        >
          <Menu className="h-6 w-6 text-ink" />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-sand border-r border-line">
            <button
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white"
              onClick={() => setOpen(false)}
            >
              <X className="h-5 w-5 text-ink" />
            </button>
            <SidebarContent {...sidebarProps} />
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0 min-w-0">
        <div className="p-4 sm:p-6 lg:p-8 xl:p-10 max-w-[1600px] mx-auto w-full">
          <Outlet />
        </div>
      </main>
      <ChatBubble />
    </div>
  );
}
